import React, { useEffect, useState } from "react";
import EmployerUser from "../../../../apiResources/EmployerUser";
import Employer from "../../../../apiResources/Employer";
import SettingsLayout from "../../../layouts/SettingsLayout";
import SettingsHeading from "../../../settings/SettingsHeading";
import Loader from "../../../library/Loader";
import { Table, Thead, Th, Tr, Td } from "../../../library/Table";
import RecruiterUser from "../../../../apiResources/RecruiterUser";
import Recruiter from "../../../../apiResources/Recruiter";
import ResendInvite from "../../../../apiResources/ResendInvite";
import {
  usePendingPartnerTeamMemberRequests,
  useTeamMembers,
} from "../../../../api/company";
import SearchInput from "../../../library/SearchInput";
import SearchBar from "../../../library/SearchBar";
import { useFlash, useModal, usePagination } from "../../../../hooks";
import Pagination from "../../../library/Pagination";
import useQueryParam from "../../../../hooks/useQueryParam";
import InfiniteProgress from "../../../library/InfiniteProgress";
import Button from "../../../library/Button";
import FlashMessage from "../../../shared/general/FlashMessage";
import StatusTag from "../../../StatusTag";
import RemoveTeamMemberModal from "../../../RemoveTeamMemberModal";
import InviteTeamMemberModal from "../../../InviteTeamMemberModal";
import RequestPartnerTeamMemberModal from "../../../RequestPartnerTeamMemberModal";
import ButtonGroup from "../../../library/ButtonGroup";
import styled from "styled-components";

const Members = (props: Props) => {
  const { user, company } = props;
  const inviteModal = useModal();
  const partnerRequestModal = useModal();
  const inviteFlash = useFlash();
  const partnerRequestFlash = useFlash();
  const isPartnerUser = company instanceof Recruiter && company.partner;

  if (!user || !company) {
    return (
      <SettingsLayout user={user} company={company}>
        <Loader />
      </SettingsLayout>
    );
  }

  return (
    <SettingsLayout user={user} company={company}>
      {!isPartnerUser && (
        <FlashMessage
          showFlashMessage={inviteFlash.visible}
          success
          message="Invite successfully sent"
        />
      )}
      {isPartnerUser && (
        <FlashMessage
          showFlashMessage={partnerRequestFlash.visible}
          success
          message="Request sent. Your HiringHub account manager will email you when it is reviewed."
        />
      )}

      <SettingsHeading>Team Members</SettingsHeading>

      {isPartnerUser ? (
        <>
          <PartnerNotice id="partner-invite-notice">
            Request to add someone to your team. HiringHub account managers on
            your company receive an email for each request and can approve or
            decline it in Account Manager.
          </PartnerNotice>
          <ButtonGroup spaceBelow align="right">
            <Button secondary onClick={partnerRequestModal.open}>
              Request team member
            </Button>
          </ButtonGroup>
          <RequestPartnerTeamMemberModal
            isOpen={partnerRequestModal.isOpen}
            onDismiss={partnerRequestModal.close}
            onSuccess={() => {
              partnerRequestModal.close();
              partnerRequestFlash.show();
            }}
          />
        </>
      ) : (
        <>
          <ButtonGroup spaceBelow align="right">
            <Button secondary onClick={inviteModal.open}>
              Invite Team Member
            </Button>
          </ButtonGroup>

          <InviteTeamMemberModal
            isOpen={inviteModal.isOpen}
            onDismiss={inviteModal.close}
            onSuccess={() => {
              inviteModal.close();
              inviteFlash.show();
            }}
          />
        </>
      )}

      <TeamMembersTable
        companyId={String(user.companyId)}
        isRecruiter={user.isRecruiter}
        isPartnerUser={isPartnerUser}
        currentUserId={String(user.id)}
      />
    </SettingsLayout>
  );
};

type TeamMembersTableProps = {
  companyId: string;
  isRecruiter: boolean;
  isPartnerUser: boolean;
  currentUserId: string;
};

const TeamMembersTable = (props: TeamMembersTableProps) => {
  const [query, setQuery] = useQueryParam<string>("");
  const [memberToRemove, setMemberToRemove] = useState<
    RecruiterUser | EmployerUser
  >(null);
  const pagination = usePagination();
  const removeModal = useModal();
  const successFlash = useFlash();
  const [sentIds, setSentIds] = useState<string[]>([]);

  const { companyId, isRecruiter, isPartnerUser, currentUserId } = props;
  const {
    data,
    isLoading: loadingTeamMembers,
    isIdle,
    isFetching,
  } = useTeamMembers({
    query,
    page: pagination.page,
    companyId,
    isRecruiter,
  });
  const { data: pendingRequests = [], isLoading: loadingPendingRequests } =
    usePendingPartnerTeamMemberRequests(isPartnerUser);
  const setPage = pagination.setPage;

  useEffect(() => {
    setPage(1);
  }, [query, setPage]);

  const isLoading =
    loadingTeamMembers || (isPartnerUser && loadingPendingRequests);
  const filteredPendingRequests = pendingRequests.filter((request) => {
    const queryValue = (query ?? '').toLowerCase().trim();
    if (!queryValue) return true;

    const fullName = `${request.first_name} ${request.last_name}`.toLowerCase();
    return (
      fullName.includes(queryValue) ||
      request.email.toLowerCase().includes(queryValue)
    );
  });

  if (isLoading || isIdle) {
    return <Loader />;
  }

  return (
    <div>
      <RemoveTeamMemberModal
        user={memberToRemove}
        isOpen={removeModal.isOpen}
        onDismiss={removeModal.close}
        onSuccess={() => {
          removeModal.close();
          successFlash.show();
          setQuery("");
        }}
      />

      <FlashMessage
        showFlashMessage={successFlash.visible}
        success
        message="User removed from your organisation"
      />

      <SearchBar>
        <SearchInput
          id="team-members-search"
          placeholder="Find a team member"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </SearchBar>
      <InfiniteProgress isLoading={isFetching} />
      <Table id="team-members-table">
        <Thead>
          <Tr>
            <Th colSpan={5}>Members</Th>
          </Tr>
        </Thead>
        <tbody>
          {data.members.length === 0 &&
            filteredPendingRequests.length === 0 && (
              <Tr>
                <Td>Sorry, there are no members that match your search</Td>
              </Tr>
            )}
          {isPartnerUser &&
            filteredPendingRequests.map((request) => (
              <Tr key={`pending-${request.id}`}>
                <Td>{`${request.first_name} ${request.last_name}`}</Td>
                <Td>{request.email}</Td>
                <Td>
                  <StatusTag status="info">Pending approval</StatusTag>
                </Td>
                <Td>{request.admin ? "Admin" : "Member"}</Td>
                <Td>
                  <StatusTag status="info">Awaiting review</StatusTag>
                </Td>
              </Tr>
            ))}
          {data.members.map((member) => (
            <Tr key={member.id}>
              <Td>{member.fullName}</Td>
              <Td>{member.email}</Td>
              <Td>
                {!member.invitationPending && (
                  <StatusTag status="success">Verified</StatusTag>
                )}
                {member.invitationPending &&
                  !isPartnerUser &&
                  (sentIds.includes(member.id) ? (
                    <div>Invite sent!</div>
                  ) : (
                    <Button
                      id="resend-invite-button"
                      secondary
                      size="sm"
                      onClick={async () => {
                        const invite = new ResendInvite({ userId: member.id });
                        invite.save();
                        setSentIds((prev) => [...prev, member.id]);
                      }}
                    >
                      <NoWrap>Resend Invite</NoWrap>
                    </Button>
                  ))}
                {member.invitationPending && isPartnerUser && (
                  <StatusTag status="info">Invitation Pending</StatusTag>
                )}
              </Td>
              <Td>{member.role}</Td>
              <Td>
                {member.id === currentUserId ? (
                  <StatusTag status="info">Your account</StatusTag>
                ) : (
                  <Button
                    id="show-remove-modal-button"
                    primary
                    size="sm"
                    onClick={() => {
                      removeModal.open();
                      setMemberToRemove(member);
                    }}
                  >
                    Remove
                  </Button>
                )}
              </Td>
            </Tr>
          ))}
        </tbody>
      </Table>
      <Pagination pagination={pagination} total={data.meta.total} />
    </div>
  );
};

type Props = {
  user: EmployerUser | RecruiterUser;
  company: Employer | Recruiter;
};

const NoWrap = styled.div`
  white-space: nowrap;
`;

const PartnerNotice = styled.div`
  padding: 12px 16px;
  margin-bottom: 16px;
  background-color: #f0f4f8;
  border: 1px solid #d0d7de;
  border-radius: 4px;
  color: #444;
  font-size: 14px;
`;

export default Members;
